import { useStateMachine } from "little-state-machine";
import React, {
  createContext,
  useCallback,
  useEffect,
  useContext,
  useMemo,
  useState,
} from "react";
import { getCartItemsAndActiveFund } from "../services/cart.service";
import useQueryParams from "./useQueryParams";
import useToast from "./useToast";
import {
  getCampaignPath,
  setCampaignPath,
  getWebformToken,
  resetCampaignFundSelected,
} from "../services/funds.service";

export const Context = createContext(null);

export default function AppContext({ children }) {
  const query = useQueryParams();
  const [funds, setFunds] = useState([]);
  const [webformToken, setWebformToken] = React.useState(getWebformToken());
  const [loadOrderItemList, setLoadOrderItemList] = useState(
    !query.aid && !query.dids
  );
  const [items, setItems] = useState([]);
  const [total, _setTotal] = useState(null);
  const totalRef = React.useRef(total);
  const setTotal = (data) => {
    totalRef.current = data;
    _setTotal(data);
  };
  const [paymentType, setPaymentType] = useState(null);
  const [frequency, _setFrequency] = useState(query.frequency || "once");
  const frequencyRef = React.useRef(frequency);
  const setFrequency = (data) => {
    frequencyRef.current = data;
    _setFrequency(data);
  };
  const [cart, setCart] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingNewItem, setIsLoadingNewItem] = useState(false);
  const { state } = useStateMachine();
  const [search, setSearch] = useState(query.q);
  const checkoutDetails = state.checkoutDetails;
  const [editStateItems, setEditStateItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState([]);
  const [shareLink, setShareLink] = useState(null);
  const sidebarCartApp = document.getElementById("sidebar-app");
  const givingApp = document.getElementById("giving-app");
  const manageGiftApp = document.getElementById("manage-gift-app");
  const miniCartApp = Array.from(
    document.getElementsByClassName("add-to-cart-form-app")
  );
  const fundCardApp = Array.from(
    document.getElementsByClassName("fund-card__portal")
  );
  const [innerWidth, setInnerWidth] = useState(window.innerWidth);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLoaingFrequency, setIsloadingFrequency] = useState(false);

  if (!getCampaignPath()) {
    setCampaignPath();
  }
  resetCampaignFundSelected();

  let fundCategoryId,
    recurringOrderUuid = null;
  if (givingApp) {
    fundCategoryId = givingApp.getAttribute("data-fund-cat-id");
  }

  if (manageGiftApp) {
    recurringOrderUuid = manageGiftApp.getAttribute(
      "data-recurring-order-uuid"
    );
  }

  const hasPendingRequiredFields = useMemo(() => {
    const { firstName, lastName, yourEmail } = checkoutDetails;
    return [firstName, lastName, yourEmail].some((i) => !i);
  }, [checkoutDetails]);

  const isCheckoutEnabled =
    useMemo(
      () => cart !== undefined && cart?.attributes.state !== "completed",
      [cart]
    ) && !hasPendingRequiredFields;

  const shouldGoThankYou = useMemo(
    () => cart?.attributes.state === "completed"
  );

  const setCartState = useCallback(
    (state) => {
      setCart({
        ...cart,
        attributes: { ...(cart?.attributes || {}), state },
      });
    },
    [cart]
  );

  const toggleMenu = useCallback(
    (event) => {
      event && event.preventDefault();
      setIsMenuOpen(!isMenuOpen);
    },
    [isMenuOpen]
  );

  const openMenu = useCallback(
    (event) => {
      event && event.preventDefault();
      !isMenuOpen && setIsMenuOpen(true);
    },
    [isMenuOpen]
  );

  async function fetchCart() {
    const notifyFetchCartItemsAndFundError = useToast({
      type: "error",
      id: "cartItemsAndFund",
      actionText: "RETRY?",
      action: async () => await fetchCart(),
      dismissable: true,
      message: "There was a problem loading your cart.",
    });
    setIsLoading(true);
    setLoadOrderItemList(false);
    return getCartItemsAndActiveFund()
      .then(({ listItems, cartInfo }) => {
        setIsLoading(false);
        setLoadingItems([]);
        if (listItems) {
          setItems(listItems.data);
        }
        if (cartInfo.data.length) {
          const cartData = cartInfo.data[0];
          setCart(cartData);
          setIsLoadingNewItem(false);
          setFrequency(cartData?.attributes.frequency);
          setTotal(cartData?.attributes.order_total.total.formatted);
        }
        return { listItems, cartInfo };
      })
      .catch(() => {
        notifyFetchCartItemsAndFundError();
      });
  }

  useEffect(() => {
    const onResize = () => {
      setInnerWidth(window.innerWidth);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    setIsMobile(innerWidth < 768);
  }, [innerWidth]);

  // Fetch cart every time items change and is not loading
  useEffect(() => {
    const shouldFetchCart = loadOrderItemList && !isLoading;
    console.debug("fetch cart?", shouldFetchCart);
    console.debug(" cartId", cart ? cart.id : null);
    console.debug(" loadOrderItemList", loadOrderItemList);
    console.debug(" isLoading", isLoading);
    if (shouldFetchCart) {
      fetchCart();
    }
  }, [loadOrderItemList, isLoading]);

  useEffect(() => {
    const op = isMenuOpen ? "add" : "remove";
    document.body.classList[op](`sidebar-open`);
  }, [isMenuOpen]);

  const contextValue = {
    isMenuOpen,
    isMobile,
    funds,
    setFunds,
    loadOrderItemList,
    setLoadOrderItemList,
    total,
    setTotal,
    frequency,
    setFrequency,
    search,
    setSearch,
    toggleMenu,
    openMenu,
    setCart,
    cart,
    setCartState,
    isCheckoutEnabled,
    isLoading,
    items,
    setItems,
    paymentType,
    setPaymentType,
    setIsLoading,
    hasPendingRequiredFields,
    shouldGoThankYou,
    sidebarCartApp,
    fundCategoryId,
    miniCartApp,
    fundCardApp,
    givingApp,
    manageGiftApp,
    recurringOrderUuid,
    editStateItems,
    setEditStateItems,
    isLoadingNewItem,
    setIsLoadingNewItem,
    loadingItems,
    setLoadingItems,
    shareLink,
    setShareLink,
    isLoaingFrequency,
    setIsloadingFrequency,
    innerWidth,
    setInnerWidth,
    totalRef,
    frequencyRef,
    webformToken,
    setWebformToken,
    fetchCart,
    stateChangeHandler: (newState) => setIsMenuOpen(newState.isOpen),
  };

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}

export const useAppContext = () => useContext(Context);
